import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useCreateCampaign, useUpdateCampaignWizard } from 'entities/campaign';
import { Button } from 'shared/ui/Button';
import { FormErrors } from 'shared/ui/FormErrors';
import { Input } from 'shared/ui/Input';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { FormSelect } from 'shared/ui/Select';
import type { AuthenticatedContractorModel } from 'entities/profile';
import { useGetUser } from 'entities/profile';
import { getDbaOptions } from 'entities/contractor';
import { AlertBox } from 'shared/ui/AlertBox';
import { logError } from 'shared/lib/log-errors';

import { useCampaignId, useCampaignName, useCreateCampaignStoreActions, useDbaId } from './CreateCampaignStoreHook';

type StepNameProps = {
  onCancel: () => void;
  onNext: () => void;
};
type FormType = {
  name: string;
  dbaId?: string;
};

const INPUT_NAME = 'name';
const SELECT_NAME = 'dbaId';

export function StepName(props: StepNameProps) {
  const { onCancel, onNext } = props;
  const campaignId = useCampaignId();
  const campaignName = useCampaignName();
  const initialDbaId = useDbaId();
  const { updateStepData } = useCreateCampaignStoreActions();
  const createCampaignQuery = useCreateCampaign();
  const updateCampaignQuery = useUpdateCampaignWizard(campaignId);
  const methods = useForm<FormType>({
    defaultValues: {
      name: campaignName,
      dbaId: initialDbaId?.toString(),
    },
  });
  const { handleSubmit, control } = methods;
  const inputValue = useWatch({ control, name: INPUT_NAME }).trim();
  const dbaId = methods.watch(SELECT_NAME);

  const userData = useGetUser<AuthenticatedContractorModel>().data?.data;

  const onSubmit = handleSubmit(async ({ name, dbaId }: FormType) => {
    const isCampaignCreated = campaignId !== 0;
    const isEmptyName = name === '';
    const dbaIdToSend = dbaId ? Number(dbaId) : undefined;
    const payload = { payload: { name, dbaId: dbaIdToSend } };

    createCampaignQuery.reset();
    updateCampaignQuery.reset();

    try {
      if (!isCampaignCreated && !isEmptyName) {
        const { data } = await createCampaignQuery.mutateAsync(payload);

        updateStepData({ campaignId: data.id });
      } else {
        await updateCampaignQuery.mutateAsync(payload);
      }

      updateStepData({ name, dbaId: dbaIdToSend });
      onNext();
    } catch (e) {
      logError(e);
    }
  });

  const dbaOptions = getDbaOptions(userData?.companyName, userData?.dbas);
  const hasDbas = dbaOptions.length > 1;
  const isNextDisabled = hasDbas ? !inputValue || !dbaId : !inputValue;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className="text-center text-xl font-medium tracking-normal">Provide details for your campaign</div>
        <Input
          className="mt-8"
          name={INPUT_NAME}
          title="Campaign name"
          type="text"
          isLightPlaceholder
          {...getDataAutoTestAttributes(['input-create-wizard-name'])}
        />
        {hasDbas && (
          <>
            <div className="mt-4 flex justify-center">
              <FormSelect
                isRequired
                title={dbaId ? 'DBA/company name' : 'Select a company for this campaign'}
                name={SELECT_NAME}
                wrapperClass="w-full"
                options={dbaOptions}
                selectedOptionId={initialDbaId?.toString()}
                isLightPlaceholder
                {...getDataAutoTestAttributes(['select-dba-name'])}
              />
            </div>
            <AlertBox
              className="mb-4 mt-2 border-deep-blue-100"
              iconClass="text-deep-blue-500"
              bodyClass="w-full"
              iconName="info"
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-normal text-slate-700">
                  The DBA/company name will be displayed to the homeowner, informing them who will be in contact about
                  the project.
                </div>
              </div>
            </AlertBox>
          </>
        )}
        <div className="mt-8 flex justify-between">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline-second"
            {...getDataAutoTestAttributes(['button-create-wizard-cancel'])}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isNextDisabled}
            loading={createCampaignQuery.isLoading || updateCampaignQuery.isLoading}
            {...getDataAutoTestAttributes(['button-create-wizard-next'])}
          >
            Next
          </Button>
        </div>
        <FormErrors error={createCampaignQuery.error || updateCampaignQuery.error} />
      </form>
    </FormProvider>
  );
}
